import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GhostIcon } from "@phosphor-icons/react";

const BG_URL =
  "https://static.prod-images.emergentagent.com/jobs/b31b23f3-02f4-45a2-876e-3a1e8855c361/images/ef98a2ccfeb39d23f2ae0be25240f8525ffe6ca7558eddf2b897fda8c6817d5b.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      await login(password);
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden"
      data-testid="login-page"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-50"
        style={{ backgroundImage: `url(${BG_URL})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
      <div className="grain" />

      <div className="relative z-10 w-full max-w-sm mx-auto px-6 fade-up">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-9 h-9 rounded-sm border border-zinc-700 bg-zinc-950 flex items-center justify-center">
            <GhostIcon size={20} weight="duotone" className="text-white" />
          </div>
          <div>
            <div className="font-overline text-[10px] text-zinc-500">
              Personal Agent
            </div>
            <div className="font-heading text-xl font-medium tracking-tight">
              DoppelGanger
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className="font-overline text-[10px] text-zinc-500 mb-3">
            Restricted Access
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tighter leading-tight">
            Wake your <span className="text-zinc-500">shadow</span>.
          </h1>
          <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
            Single-user platform. Enter your access phrase to bring the agent
            online.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="font-overline text-[10px] text-zinc-500 block mb-2">
              Access Phrase
            </label>
            <Input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              placeholder="••••••••"
              className="rounded-sm bg-black border-zinc-800 text-zinc-100 focus-visible:ring-1 focus-visible:ring-zinc-500"
            />
          </div>

          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-white text-black hover:bg-zinc-200 rounded-sm font-medium"
          >
            {loading ? "Authenticating..." : "Enter"}
          </Button>
        </form>

        <div className="mt-10 font-mono text-[10px] text-zinc-600">
          v0.1.0 · build {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
